import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProgressIndicator, useTheme } from '@veneer/core';

import ApiError from '../../types/error';
import { CategoryType } from '../../types/devices';

import { OperationStatus } from '../../types';
import { CategoryDeviceTypeMap } from '../../hooks/useDeviceCategoryMapping';
import { RootState } from '../../store';

import { getDeviceCategoriesRequest } from '../../features/devices/devices-slice';
import { getDeviceTelemetryRequest } from '../../features/telemetry/device-telemetry-slice';

import CategoryItem from '../category-item';
import { ErrorDisplay } from '../common';
import { TelemetryError } from '../category-item/styled';

export type CategoryListProps = {
  isCategoryLoading: boolean;
  error: ApiError | null;
  categoryDeviceTypeMap: CategoryDeviceTypeMap;
  filteredCategoryTypes: CategoryType[];
};

const CategoryList: React.FC<CategoryListProps> = ({
  isCategoryLoading,
  error,
  categoryDeviceTypeMap,
  filteredCategoryTypes
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    getDeviceTelemetry: {
      error: getDeviceTelemetryError,
      status: getDeviceTelemetryStatus
    }
  } = useSelector((state: RootState) => state.DeviceTelemetryReducer);

  const handleRetryCategories = () => {
    dispatch(getDeviceCategoriesRequest());
  };

  const handleRetryTelemetry = () => {
    dispatch(getDeviceTelemetryRequest());
  };

  if (isCategoryLoading) {
    return (
      <ProgressIndicator
        appearance="linear"
        behavior="indeterminate"
        size="small"
        data-testid="loading"
      />
    );
  }
  if (error) {
    return (
      <ErrorDisplay
        dataTestId={
          'react-device-management-mfe-device-details-viewer-category-error'
        }
        error={error}
        retryAction={handleRetryCategories}
      />
    );
  }
  if (Object.keys(categoryDeviceTypeMap).length > 0) {
    return (
      <>
        {getDeviceTelemetryStatus === OperationStatus.ERRORED && (
          <TelemetryError
            data-testid="react-device-management-mfe-device-details-viewer-telemetry-error-container"
            $veneerTheme={theme}
          >
            <ErrorDisplay
              dataTestId={
                'react-device-management-mfe-device-details-viewer-telemetry-error'
              }
              error={getDeviceTelemetryError}
              retryAction={handleRetryTelemetry}
            />
          </TelemetryError>
        )}
        {filteredCategoryTypes.map((category, index) => (
          <CategoryItem
            key={category}
            dataTestId={`react-device-management-mfe-device-details-viewer-category-${category}`}
            type={category}
            subTypeMap={categoryDeviceTypeMap[category]}
            isLastCategory={index === filteredCategoryTypes.length - 1}
          />
        ))}
      </>
    );
  }
  return null;
};

export default CategoryList;
